import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Heading, Message, toaster } from "rsuite";
import { useTranslation } from "react-i18next";

import constants from "utils/constants";

import { getErrorMessage } from "api/defaults";
import {
    loadCustomerDetails,
    updateCustomerDetails,
} from "api/customers/customer-details-api";

import useRefreshPage from "hooks/useRefreshPage";

import {
    ICustomerDetails,
    ConsentStatusType,
    IEmailDetails,
    ICustomerPartner,
    CustomerSubpage,
} from "store/customers/customer-details/customer-details-types";
import { ICustomer } from "store/customers/customers-types";

import SendEmailContainer from "components/SendEmail/SendEmailContainer";
import CustomerNavigationContainer from "./CustomerNavigation/CustomerNavigationContainer";
import GeneralDetailsContainer from "./GeneralDetails/GeneralDetailsContainer";
import PackageDeliveryContainer from "./PackageDelivery/PackageDeliveryContainer";
import CustomerHistoryContainer from "./CustomerHistory/CustomerHistoryContainer";
import ErrorSectionContainer from "components/Common/ErrorSection/ErrorSectionContainer";

import css from "./CustomerDetails.module.css";
import "themes/required.less";

interface IComponentProps {
    errorMessage: string | null;
    customerDetails: ICustomerDetails | null;
    activeCustomer: ICustomer;
    customers: ICustomer[];
    fetchCustomers: (customerId?: string) => void;
    setCustomerDetails: (customerDetails: ICustomerDetails | null) => void;
    setIsUnsavedChangeAvailable: (isUnsavedChangeAvailable: boolean) => void;
    setActiveCustomerById: (customerId: string) => void;
    setActiveCustomerName: (name: string) => void;
    sendEmailWithLink: (
        customerId: string,
        emailDetails: IEmailDetails
    ) => void;
    setEmailSent: (isEmailSent: boolean) => void;
    setLoaderVisibility: (visible: boolean) => void;
    setErrorResponseMessage: (error: string) => void;
}

const CustomerDetails: React.FC<IComponentProps> = ({
    activeCustomer,
    errorMessage,
    customerDetails,
    customers,
    fetchCustomers,
    setCustomerDetails,
    setIsUnsavedChangeAvailable,
    setActiveCustomerById,
    setActiveCustomerName,
    sendEmailWithLink,
    setEmailSent,
    setLoaderVisibility,
    setErrorResponseMessage,
}: IComponentProps) => {
    const { t } = useTranslation();
    const { customerId } = useParams<{
        customerId: string;
    }>();
    const containerRef = useRef<HTMLDivElement>(null);
    const [isSendByEmailClicked, setIsSendByEmailClicked] = useState(false);
    const [allConsentsPending, setAllConsentsPending] = useState(false);
    const [customerPartners, setCustomerPartners] = useState<
        ICustomerPartner[]
    >([]);
    const [activeTab, setActiveTab] = useState<string>(CustomerSubpage.General);
    const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
    const [originalDetails, setOriginalDetails] =
        useState<ICustomerDetails | null>(customerDetails);
    const [areDetailsFetched, setAreDetailsFetched] = useState<boolean>(false);

    const title = t("customerDetails.pageTitle");

    const fetchCustomerDetails = () => {
        if (customerId) {
            setLoaderVisibility(true);
            loadCustomerDetails(customerId).then(([payload, status]) => {
                setLoaderVisibility(false);
                if (payload && status === constants.statusCode.OK) {
                    setCustomerDetails(payload);
                    setOriginalDetails(payload);
                }
                setAreDetailsFetched(true);
            });
        }
    };
    const loadCustomers = () => {
        if (!activeCustomer.id) {
            fetchCustomers(customerId);
        }
    };

    useEffect(() => {
        return () => {
            setCustomerDetails(null);
        };
    }, []);

    useEffect(loadCustomers, [fetchCustomers, activeCustomer, customerId]);

    useEffect(() => {
        if (!areDetailsFetched || activeCustomer.id !== customerId) {
            fetchCustomerDetails();
        }
    }, [customerId, activeCustomer, areDetailsFetched]);

    useEffect(() => {
        if (areDetailsFetched && customerId) {
            setActiveTab(CustomerSubpage.General);
            setActiveCustomerById(customerId);
        }
    }, [customerId, areDetailsFetched]);

    useEffect(() => {
        if (
            (!activeCustomer.id || !activeCustomer.name) &&
            customers.length &&
            customerId
        ) {
            setActiveCustomerById(customerId);
        }
    }, [activeCustomer, setActiveCustomerById, customerId, customers]);

    useEffect(() => {
        setAllConsentsPending(
            !!customerDetails?.regionConsents.every((region) =>
                region.consents.every(
                    (consent) =>
                        consent.status === ConsentStatusType.Pending ||
                        consent.status === ConsentStatusType.Failed
                )
            )
        );
        setCustomerPartners(customerDetails?.partners || []);
    }, [customerDetails]);

    useEffect(() => {
        if (customerDetails) {
            setCustomerDetails({
                ...customerDetails,
                partners: customerPartners,
            });
        }
    }, [customerPartners]);

    useEffect(() => {
        setErrorResponseMessage("");
    }, [customerId]);

    useRefreshPage(() => {
        fetchCustomerDetails();
    });

    const handleFormSubmit = (isValid: boolean) => {
        if (isValid && customerDetails && customerId) {
            setLoaderVisibility(true);
            updateCustomerDetails(customerId, customerDetails).then(
                ([result, status]) => {
                    if (status === constants.statusCode.NoContent) {
                        setIsUnsavedChangeAvailable(false);
                        setActiveCustomerName(customerDetails.name);
                        toaster.push(
                            <Message type="success" showIcon closable>
                                {t(
                                    "customerDetails.successfulUpdateAlertMessage"
                                )}
                            </Message>,
                            {
                                duration: constants.alertDurationMsec,
                            }
                        );
                    } else {
                        const errorMessage = getErrorMessage(
                            String(result),
                            status
                        );
                        toaster.push(
                            <Message type="error" showIcon closable>
                                {errorMessage}
                            </Message>,
                            {
                                duration: constants.alertDurationMsec,
                            }
                        );
                    }
                    setAreDetailsFetched(false);
                    setLoaderVisibility(false);
                }
            );
        }
    };

    if (errorMessage) {
        return <ErrorSectionContainer />;
    }
    const handleSendEmail = (envMoniker: string, details: IEmailDetails) => {
        if (customerId) {
            sendEmailWithLink(customerId, details);
        }
    };

    return customerDetails !== null ? (
        <div ref={containerRef} className={css.pageContainer}>
            <CustomerHistoryContainer />
            <SendEmailContainer
                isOpened={isSendByEmailClicked}
                setIsOpened={setIsSendByEmailClicked}
                userId={activeCustomer.id}
                sendEmailWithLink={handleSendEmail}
                setEmailSent={setEmailSent}
            />

            <Heading level={1} className={css.pageTitle}>
                {title}
            </Heading>

            <CustomerNavigationContainer
                activeTab={activeTab}
                isDataLoading={isDataLoading}
                setActiveTab={setActiveTab}
            />
            {activeTab === CustomerSubpage.General ? (
                <GeneralDetailsContainer
                    containerRef={containerRef}
                    allConsentsPending={allConsentsPending}
                    handleFormSubmit={handleFormSubmit}
                    setIsSendByEmailClicked={setIsSendByEmailClicked}
                    setCustomerPartners={setCustomerPartners}
                    setIsDataLoading={setIsDataLoading}
                />
            ) : (
                <PackageDeliveryContainer
                    containerRef={containerRef}
                    originalDetails={originalDetails}
                    handleFormSubmit={handleFormSubmit}
                    setIsDataLoading={setIsDataLoading}
                />
            )}
        </div>
    ) : null;
};

export default CustomerDetails;
